import { createContext, FC, ReactNode, useCallback, useMemo } from 'react';

import { useConfirmationDialog } from 'src/components/ConfirmationDialog/useConfirmationDialog';
import { useRequiredContext } from 'src/hooks/useRequiredContext';
import { fetchSpaces, removeSpace } from 'src/store/spaces/services';
import { useAppDispatch } from 'src/store/useAppDispatch';

export interface SpacesContextValue {
  loadMore: (startIndex: number, stopIndex: number) => Promise<void>;
  removeSpace: (id: number) => Promise<void>;
  deleteSpaceDialog: ReactNode;
}

export const SpacesContext = createContext<SpacesContextValue | undefined>(
  undefined,
);

interface SpacesProviderProps {
  children?: ReactNode;
}

export const SpacesProvider: FC<SpacesProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();

  const loadMore = useCallback(async () => {
    await dispatch(fetchSpaces(false));
  }, [dispatch]);

  const {
    dialog: deleteSpaceDialog,
    open: openDeleteSpaceDialog,
    confirmation: confirmDeleteSpace,
  } = useConfirmationDialog({
    title: 'Удалить пространство?',
    okLabel: 'Удалить',
    cancelLabel: 'Назад',
    text: 'Вы уверены, что хотите удалить пространство?',
  });

  const remove = useCallback(
    async (id: number) => {
      openDeleteSpaceDialog();
      const confirmed = await confirmDeleteSpace();

      if (!confirmed) {
        return;
      }

      await dispatch(removeSpace(id));
    },
    [confirmDeleteSpace, dispatch, openDeleteSpaceDialog],
  );

  const contextValue = useMemo((): SpacesContextValue => {
    return {
      loadMore,
      deleteSpaceDialog,
      removeSpace: remove,
    };
  }, [loadMore, deleteSpaceDialog, remove]);

  return (
    <SpacesContext.Provider value={contextValue}>
      {children}
    </SpacesContext.Provider>
  );
};

export const useSpaces = (): SpacesContextValue =>
  useRequiredContext(SpacesContext);
